import { Printer } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import type { CrmDataProvider } from "../providers/types";
import type { PublicQuote } from "../types";
import { QuoteDocument } from "./QuoteDocument";

// Kundelenken #/tilbud/<token>. Vises uten innlogging og utenfor resten av
// Mimin (se CRM.tsx), så den henter tilbudet selv via quote_public.
export const PublicQuotePage = ({
  token,
  dataProvider,
}: {
  token: string;
  dataProvider: Pick<CrmDataProvider, "getPublicQuote">;
}) => {
  const translate = useTranslate();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "missing" }
    | { kind: "ready"; quote: PublicQuote }
  >({ kind: "loading" });

  useEffect(() => {
    let active = true;
    setState({ kind: "loading" });
    dataProvider
      .getPublicQuote(token)
      .then((quote) => {
        if (!active) return;
        setState(quote ? { kind: "ready", quote } : { kind: "missing" });
      })
      .catch(() => active && setState({ kind: "missing" }));
    return () => {
      active = false;
    };
  }, [dataProvider, token]);

  useEffect(() => {
    if (state.kind !== "ready") return;
    const previous = document.title;
    document.title = `${translate("crm.quotes.document.label", { _: "Tilbud" })} #${state.quote.number} – ${state.quote.title}`;
    return () => {
      document.title = previous;
    };
  }, [state, translate]);

  return (
    <div className="public-quote min-h-screen bg-neutral-100 py-8 text-neutral-900 print:bg-white print:py-0">
      {state.kind === "loading" ? (
        <p className="text-center text-sm text-neutral-500">
          {translate("crm.quotes.public.loading", { _: "Henter tilbudet …" })}
        </p>
      ) : state.kind === "missing" ? (
        <div className="mx-auto max-w-md rounded-lg bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold">
            {translate("crm.quotes.public.missing_title", {
              _: "Fant ikke tilbudet",
            })}
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            {translate("crm.quotes.public.missing", {
              _: "Lenken er feil, eller tilbudet er trukket tilbake. Ta kontakt med den som sendte deg lenken.",
            })}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="mx-auto flex w-full max-w-[760px] justify-end px-4 print:hidden">
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-neutral-900 px-4 text-sm font-medium text-white hover:bg-neutral-800"
            >
              <Printer className="h-4 w-4" />
              {translate("crm.quotes.public.print", {
                _: "Last ned som PDF",
              })}
            </button>
          </div>
          <div className="shadow-sm print:shadow-none">
            <QuoteDocument quote={state.quote} />
          </div>
        </div>
      )}
    </div>
  );
};
